import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Trophy, Play, Pause, SkipForward, Square, Eye, EyeOff, Zap, Tv, Users } from 'lucide-react';
import './ChampionsArena.css';

// ─────────────────────────  TV (apresentação 16:9) ─────────────────────────
export function ArenaTV() {
  const { code } = useParams();
  const [state, setState] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let active = true;
    const fetchState = async () => {
      try {
        const r = await api.get(`/champions/arena/sessions/${code}/state`);
        if (active) setState(r.data);
      } catch { /* noop */ }
    };
    fetchState();
    const id = setInterval(fetchState, 1500);
    return () => { active = false; clearInterval(id); };
  }, [code]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!state) return <div className="arena-bg flex items-center justify-center text-2xl">Carregando arena…</div>;

  const joinUrl = `${window.location.origin}/arena/aluno/${code}`;
  const top = (state.ranking || []).slice(0, 10);
  const ended = state.status === 'ended';

  return (
    <div className="arena-bg arena-grain min-h-screen">
      {ended && Array.from({ length: 30 }).map((_, i) => (
        <div key={i} className="arena-confetti" style={{
          left: `${Math.random() * 100}%`,
          background: ['#f43f5e','#fbbf24','#22c55e','#6366f1','#06b6d4'][i % 5],
          animationDelay: `${Math.random() * 2}s`,
        }} />
      ))}

      <div className="max-w-[1800px] mx-auto p-10 grid grid-cols-12 gap-8 min-h-screen">
        {/* Header */}
        <div className="col-span-12 flex items-center justify-between">
          <div className="space-y-1">
            <div className="arena-pill"><Zap className="w-3.5 h-3.5" /> Desafio dos Campeões</div>
            <h1 className="arena-h1 text-5xl">{state.battle_titulo}</h1>
            <div className="text-slate-400 text-lg">
              {state.battle_disciplina || '—'} · {state.class_name || 'Turma'} · Questão {state.current_index + 1} de {state.total_questions}
            </div>
          </div>
          <div className="text-right">
            <div className="arena-pill"><Users className="w-3.5 h-3.5" /> {state.participants_count} participantes</div>
            <div className="text-3xl font-mono mt-2">{new Date().toLocaleTimeString('pt-BR')}</div>
          </div>
        </div>

        {/* Centro: questão */}
        <div className="col-span-8 arena-card arena-glow p-12 flex flex-col justify-between">
          {state.status === 'lobby' ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
              <div className="arena-pill">Aguardando o início</div>
              <h2 className="arena-question">Entre pelo celular para participar.</h2>
              <p className="text-slate-400 text-xl">O professor iniciará em instantes.</p>
            </div>
          ) : state.status === 'ended' ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
              <Trophy className="w-32 h-32 text-amber-400 arena-pulse" />
              <h2 className="arena-question">Fim do Desafio!</h2>
              {top[0] && (
                <div className="text-3xl">
                  Campeão: <strong className="text-amber-300">{top[0].name}</strong> · {top[0].score} pts
                </div>
              )}
            </div>
          ) : state.current_question ? (
            <>
              <div>
                <div className="arena-pill mb-4">
                  {state.status === 'paused' ? 'PAUSADO' : 'AO VIVO'} · QUESTÃO {state.current_index + 1}
                </div>
                <div className="arena-question mb-6">{state.current_question.enunciado}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {['a','b','c','d'].map((l) => {
                  const txt = state.current_question[`alternativa_${l}`];
                  if (!txt) return null;
                  return (
                    <div key={l} className="arena-alt">
                      <div className="letter">{l.toUpperCase()}</div>
                      <div className="text-2xl font-semibold">{txt}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
          {/* Barra de progresso */}
          <div className="mt-6">
            <div className="arena-bar"><div style={{ width: `${Math.min(100, ((state.current_index + (state.status==='ended'?1:0)) / Math.max(1, state.total_questions)) * 100)}%` }} /></div>
          </div>
        </div>

        {/* Ranking */}
        <div className="col-span-4 arena-card p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-amber-400" />
            <h3 className="text-2xl font-bold">Ranking</h3>
            <span className="ml-auto text-slate-400 text-sm">Top {top.length}</span>
          </div>
          {top.length === 0 ? (
            <div className="text-slate-500 text-center my-auto py-12">Sem participantes ainda.</div>
          ) : (
            <div className="space-y-2 overflow-y-auto">
              {top.map((p, i) => (
                <div key={p.id} className={`flex items-center gap-3 px-3 py-2 rounded-xl ${i === 0 ? 'bg-gradient-to-r from-amber-500/20 to-amber-700/0 border border-amber-400/30' : 'bg-white/5'}`}>
                  <div className={`w-9 h-9 rounded-lg grid place-items-center font-extrabold ${i === 0 ? 'bg-amber-400 text-slate-900' : i === 1 ? 'bg-slate-300 text-slate-900' : i === 2 ? 'bg-orange-400 text-slate-900' : 'bg-white/10 text-white'}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : (i + 1)}
                  </div>
                  <div className="flex-1 truncate font-semibold">{p.name}</div>
                  <div className="font-mono text-amber-300">{Math.round(p.score)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer: QR + código */}
        <div className="col-span-12 flex items-center justify-between arena-card p-6">
          <div>
            <div className="text-slate-400 text-sm uppercase tracking-widest">Para entrar</div>
            <div className="text-3xl font-bold mt-1">{joinUrl}</div>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-slate-400 text-sm uppercase tracking-widest text-right">Código</div>
              <div className="text-6xl font-extrabold font-mono text-amber-300">{state.code}</div>
            </div>
            <div className="bg-white p-3 rounded-xl">
              <QRCodeCanvas value={joinUrl} size={140} level="M" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────  MOBILE (aluno) ─────────────────────────
export function ArenaAluno() {
  const { code } = useParams();
  const [name, setName] = useState('');
  const [pid, setPid] = useState(() => localStorage.getItem(`arena_pid_${code}`) || '');
  const [pname, setPname] = useState(() => localStorage.getItem(`arena_name_${code}`) || '');
  const [state, setState] = useState(null);
  const [answering, setAnswering] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [powers, setPowers] = useState({ used: { relampago: false, dobro: false }, active: { relampago: false, dobro: false } });
  const lastQid = useRef('');

  useEffect(() => {
    if (!pid) return;
    let active = true;
    const fetchState = async () => {
      try {
        const r = await api.get(`/champions/arena/sessions/${code}/state?participant_id=${pid}`);
        if (active) {
          setState(r.data);
          const qid = r.data?.current_question?.id;
          if (qid && qid !== lastQid.current) {
            lastQid.current = qid;
            setLastResult(null);
          }
        }
      } catch { /* noop */ }
    };
    fetchState();
    const id = setInterval(fetchState, 1500);
    return () => { active = false; clearInterval(id); };
  }, [code, pid]);

  const joinNow = async () => {
    if (!name.trim()) { toast.error('Digite seu nome.'); return; }
    try {
      // Fase 2: passa student_id do usuário logado (se houver) para troféus/stats
      const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const sid = user.student_id || user.id || null;
      const r = await api.post(`/champions/arena/sessions/${code}/join`, { name: name.trim(), student_id: sid });
      setPid(r.data.participant_id);
      setPname(r.data.name);
      localStorage.setItem(`arena_pid_${code}`, r.data.participant_id);
      localStorage.setItem(`arena_name_${code}`, r.data.name);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao entrar.'); }
  };

  const activatePower = async (power) => {
    if (powers.used[power] || powers.active[power]) return;
    try {
      await api.post(`/champions/arena/sessions/${code}/use-power`, { participant_id: pid, power });
      setPowers((p) => ({ ...p, active: { ...p.active, [power]: true } }));
      toast.success(power === 'relampago' ? '⚡ Resposta Relâmpago armado!' : '🔥 Rodada Dobro armada!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao ativar poder.'); }
  };

  const answer = async (letter) => {
    if (!state?.current_question || answering) return;
    setAnswering(true);
    try {
      const r = await api.post(`/champions/arena/sessions/${code}/answer`, { participant_id: pid, answer: letter });
      setLastResult(r.data);
      // Atualiza estado dos poderes (consome ativações)
      const applied = r.data.powers_applied || [];
      setPowers((p) => ({
        used: {
          relampago: p.used.relampago || applied.includes('relampago'),
          dobro: p.used.dobro || applied.includes('dobro'),
        },
        active: { relampago: false, dobro: false },
      }));
      if (r.data.correct) toast.success(`+${Math.round(r.data.score_delta)} pts`); else toast.error('Resposta enviada');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao responder.'); }
    finally { setAnswering(false); }
  };

  if (!pid) {
    return (
      <div className="arena-mobile">
        <div className="my-auto space-y-6 text-center">
          <div className="arena-pill mx-auto"><Zap className="w-3.5 h-3.5" /> Desafio dos Campeões</div>
          <h1 className="text-4xl font-extrabold">Sala #{code}</h1>
          <div className="space-y-3 max-w-sm mx-auto">
            <input
              className="w-full bg-white/10 border-2 border-white/20 rounded-2xl p-5 text-2xl text-center font-semibold"
              placeholder="Seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && joinNow()}
              data-testid="arena-aluno-nome"
            />
            <button className="arena-mobile-btn bg-gradient-to-br from-rose-500 to-amber-500 border-rose-400" onClick={joinNow} data-testid="arena-aluno-entrar">
              Entrar na sala
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = state?.current_question;
  const answered = state?.already_answered || !!lastResult;

  return (
    <div className="arena-mobile">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-widest">Você</div>
          <div className="text-2xl font-extrabold">{pname}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400 uppercase tracking-widest">Sala</div>
          <div className="font-mono text-amber-300 text-2xl">{code}</div>
        </div>
      </div>

      {state?.status === 'lobby' && (
        <div className="my-auto text-center space-y-3">
          <div className="text-2xl font-semibold">Aguardando o professor iniciar…</div>
          <div className="text-slate-400">{state.participants_count} participantes na sala</div>
        </div>
      )}

      {state?.status === 'ended' && (
        <div className="my-auto text-center space-y-3">
          <Trophy className="w-24 h-24 mx-auto text-amber-300 arena-pulse" />
          <div className="text-3xl font-extrabold">Fim do desafio!</div>
          {(state.ranking || []).find((r) => r.id === pid) && (
            <div className="text-xl">
              Você ficou em <strong>{(state.ranking || []).find((r) => r.id === pid).rank}º</strong> ·{' '}
              {Math.round((state.ranking || []).find((r) => r.id === pid).score)} pts
            </div>
          )}
        </div>
      )}

      {state?.status === 'paused' && (
        <div className="my-auto text-center text-2xl">⏸ Pausado pelo professor.</div>
      )}

      {state?.status === 'playing' && q && (
        <div className="space-y-4">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="text-xs text-slate-400 uppercase tracking-widest">Questão {state.current_index + 1} de {state.total_questions}</div>
            <div className="text-xl font-semibold mt-1">{q.enunciado}</div>
          </div>

          {answered ? (
            <div className="my-auto text-center space-y-3 py-10">
              {lastResult?.correct ? (
                <div className="text-5xl">✅</div>
              ) : (
                <div className="text-5xl">📨</div>
              )}
              <div className="text-2xl font-bold">{lastResult?.correct ? `+${Math.round(lastResult.score_delta)} pts` : 'Resposta enviada'}</div>
              <div className="text-slate-400">Aguardando próxima questão…</div>
            </div>
          ) : (
            <>
              {/* Fase 2 — Poderes Especiais */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }} data-testid="arena-powers">
                <button
                  onClick={() => activatePower('relampago')}
                  disabled={powers.used.relampago || powers.active.relampago}
                  data-testid="arena-power-relampago"
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 12,
                    background: powers.active.relampago ? 'linear-gradient(135deg,#06b6d4,#0ea5e9)' : powers.used.relampago ? 'rgba(255,255,255,0.04)' : 'rgba(6,182,212,0.18)',
                    color: powers.used.relampago ? '#475569' : '#fff',
                    border: `2px solid ${powers.active.relampago ? '#22d3ee' : 'rgba(6,182,212,0.45)'}`,
                    fontWeight: 700, fontSize: 13,
                    opacity: powers.used.relampago ? 0.45 : 1,
                  }}
                >
                  ⚡ Relâmpago {powers.active.relampago ? '· ARMADO' : powers.used.relampago ? '· usado' : '+50% se <5s'}
                </button>
                <button
                  onClick={() => activatePower('dobro')}
                  disabled={powers.used.dobro || powers.active.dobro}
                  data-testid="arena-power-dobro"
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 12,
                    background: powers.active.dobro ? 'linear-gradient(135deg,#f97316,#ef4444)' : powers.used.dobro ? 'rgba(255,255,255,0.04)' : 'rgba(249,115,22,0.18)',
                    color: powers.used.dobro ? '#475569' : '#fff',
                    border: `2px solid ${powers.active.dobro ? '#fb923c' : 'rgba(249,115,22,0.45)'}`,
                    fontWeight: 700, fontSize: 13,
                    opacity: powers.used.dobro ? 0.45 : 1,
                  }}
                >
                  🔥 Dobro {powers.active.dobro ? '· ARMADO' : powers.used.dobro ? '· usado' : '2x pontos'}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {['a','b','c','d'].map((l) => {
                  const txt = q[`alternativa_${l}`];
                  if (!txt) return null;
                  return (
                    <button
                      key={l}
                      className="arena-mobile-btn text-left flex items-center gap-4"
                      onClick={() => answer(l.toUpperCase())}
                      disabled={answering}
                      data-testid={`arena-alt-${l}`}
                    >
                      <span className="w-12 h-12 rounded-xl bg-amber-400 text-slate-900 grid place-items-center text-2xl font-extrabold">{l.toUpperCase()}</span>
                      <span className="text-lg font-semibold flex-1">{txt}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────  CONTROLE DO PROFESSOR ─────────────────────────
export function ArenaControl({ code, onClose }) {
  const [state, setState] = useState(null);
  const [report, setReport] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!code) return;
    let active = true;
    const fetchState = async () => {
      try {
        const r = await api.get(`/champions/arena/sessions/${code}/state`);
        if (active) setState(r.data);
      } catch { /* noop */ }
    };
    fetchState();
    const id = setInterval(fetchState, 1500);
    return () => { active = false; clearInterval(id); };
  }, [code]);

  const act = async (path, label) => {
    try {
      await api.post(`/champions/arena/sessions/${code}/${path}`);
      toast.success(label);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  const loadReport = async () => {
    try {
      const r = await api.get(`/champions/arena/sessions/${code}/report`);
      setReport(r.data);
    } catch { /* noop */ }
  };

  const openTV = () => window.open(`/arena/${code}`, '_blank', 'noopener,noreferrer');
  const joinUrl = `${window.location.origin}/arena/aluno/${code}`;

  if (!state) return <div className="p-6 text-center text-slate-500">Carregando…</div>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Código da sala</div>
          <div className="text-3xl font-mono font-extrabold text-rose-600" data-testid="arena-control-code">{state.code}</div>
        </div>
        <div className="rounded-xl border bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div>
          <div className="text-xl font-bold mt-1">{state.status.toUpperCase()}</div>
        </div>
      </div>
      <div className="rounded-xl border p-3">
        <div className="text-xs text-slate-500">Para alunos entrarem (celular):</div>
        <div className="font-mono text-sm break-all">{joinUrl}</div>
        <div className="flex items-center justify-center mt-2 bg-white p-2 rounded-lg w-fit mx-auto">
          <QRCodeCanvas value={joinUrl} size={120} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={openTV} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm flex items-center justify-center gap-2" data-testid="arena-open-tv">
          <Tv className="w-4 h-4" /> Abrir Modo TV
        </button>
        <button
          onClick={() => window.open(`/arena/palco/${code}`, '_blank', 'noopener')}
          className="px-3 py-2 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white text-sm font-bold flex items-center justify-center gap-2"
          data-testid="arena-open-palco"
        >
          <Trophy className="w-4 h-4" /> Abrir Palco 🎯
        </button>
        <button onClick={() => act('next', 'Próxima questão')} className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm flex items-center justify-center gap-2" data-testid="arena-next">
          <SkipForward className="w-4 h-4" /> {state.status === 'lobby' ? 'Iniciar' : 'Próxima'}
        </button>
        <button onClick={() => act('pause', state.status === 'playing' ? 'Pausado' : 'Retomado')} className="px-3 py-2 rounded-lg bg-amber-500 text-white text-sm flex items-center justify-center gap-2">
          {state.status === 'playing' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {state.status === 'playing' ? 'Pausar' : 'Retomar'}
        </button>
        <button onClick={() => act('toggle-ranking', state.show_ranking ? 'Ranking ocultado' : 'Ranking visível')} className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm flex items-center justify-center gap-2">
          {state.show_ranking ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {state.show_ranking ? 'Ocultar' : 'Mostrar'} ranking
        </button>
        <button onClick={async () => { await act('end', 'Sessão encerrada'); loadReport(); }} className="col-span-2 px-3 py-2 rounded-lg bg-slate-600 text-white text-sm flex items-center justify-center gap-2">
          <Square className="w-4 h-4" /> Encerrar
        </button>
      </div>

      <div className="rounded-xl border p-3">
        <div className="font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Participantes ({state.participants_count})</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {(state.ranking || []).slice(0, 30).map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm">
              <span className="w-6 text-slate-500">{r.rank}º</span>
              <span className="flex-1 truncate">{r.name}</span>
              <span className="font-mono text-amber-700">{Math.round(r.score)}</span>
            </div>
          ))}
          {(!state.ranking || state.ranking.length === 0) && <div className="text-xs text-slate-500">Sem entradas ainda.</div>}
        </div>
      </div>

      {state.status === 'ended' && (
        <div className="rounded-xl border p-3 bg-emerald-50">
          <button onClick={loadReport} className="text-sm font-semibold text-emerald-700 underline">Carregar relatório final</button>
          {report && (
            <div className="text-sm mt-2 space-y-1">
              <div><strong>Destaque:</strong> {report.destaque?.name || '—'} ({Math.round(report.destaque?.score || 0)} pts)</div>
              <div><strong>Questão mais errada:</strong> {report.most_errored?.percent_correct ?? '—'}% acertos</div>
              <div><strong>Mais fácil:</strong> {report.most_easy?.percent_correct ?? '—'}% acertos</div>
            </div>
          )}
        </div>
      )}

      {onClose && (
        <button onClick={onClose} className="w-full text-xs text-slate-500 underline">Fechar painel</button>
      )}
    </div>
  );
}
